import { useState, useCallback, useMemo, useEffect } from 'react';

const txId = (t) => t._docId || String(t.id);

/**
 * State-Logik für manuelles Timeline-Reordering.
 * Kopiert das optimistische Patch-Pattern von useTransferLinking.
 *
 * @param {Object}   args
 * @param {Array}    args.txs     Alle geladenen Transaktionen (effectiveTxs von useTransferLinking)
 * @param {Function} args.patchTx patchTx(docId, fields) — aus useTransactions
 */
export function useTimelineReorder({ txs, patchTx }) {
  const [reorderMode,     setReorderMode]     = useState(false);
  const [pendingReorders, setPendingReorders]  = useState(new Map()); // txId → fields

  // ── Optimistische Sicht: txs + noch nicht bestätigte Reorder-Patches ────────
  const effectiveTxs = useMemo(() => {
    if (pendingReorders.size === 0) return txs;
    return txs.map(t => {
      const patch = pendingReorders.get(txId(t));
      return patch ? { ...t, ...patch } : t;
    });
  }, [txs, pendingReorders]);

  // Patch verwerfen, sobald Firestore-Snapshot den Zielzustand bestätigt
  useEffect(() => {
    setPendingReorders(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const [id, patch] of prev) {
        const real = txs.find(t => txId(t) === id);
        if (!real) { next.delete(id); changed = true; continue; }
        const reconciled = Object.keys(patch).every(
          k => (real[k] ?? null) === (patch[k] ?? null)
        );
        if (reconciled) { next.delete(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [txs]);

  // ── Reordering committen ──────────────────────────────────────────────────────
  const commitReorder = useCallback(async (id, effectiveTimestamp, manualOrderIndex) => {
    const tx = txs.find(t => txId(t) === id);
    if (!tx) return;

    const fields = {
      effectiveTimestamp: effectiveTimestamp ?? null,
      manualOrderIndex:   manualOrderIndex   ?? null,
      // originalTimestamp einmalig einfrieren
      originalTimestamp:  tx.originalTimestamp ?? `${tx.datum}T${tx.zeit || '00:00:00'}Z`,
      reorderedAt:        new Date().toISOString(),
    };

    // Optimistisches Update
    setPendingReorders(prev => new Map(prev).set(id, fields));

    if (tx._docId) await patchTx(tx._docId, fields);
  }, [txs, patchTx]);

  // ── Reorder-Modus deaktivieren ───────────────────────────────────────────────
  const exitReorderMode = useCallback(() => {
    setReorderMode(false);
  }, []);

  // ── Alle Reorderings einer TX zurücksetzen ───────────────────────────────────
  const resetReorder = useCallback(async (id) => {
    const tx = txs.find(t => txId(t) === id);
    if (!tx) return;
    const resetFields = { effectiveTimestamp: null, manualOrderIndex: null, reorderedAt: null };
    setPendingReorders(prev => new Map(prev).set(id, resetFields));
    if (tx._docId) await patchTx(tx._docId, resetFields);
  }, [txs, patchTx]);

  return {
    reorderMode, setReorderMode, exitReorderMode,
    effectiveTxs,
    commitReorder,
    resetReorder,
  };
}
